import { DtConstants } from "../schema/DtConstants";
import { FocusedTime } from "./timeline/FocusedTime";
import { ColumnFamilies, ElementIdWithFlagsEncodedLength } from "../schema/dt-schema";
import { nowSec } from "./timeline/time";
import { TimeInterval } from "./timeline/TimeInterval";

const { StreamStates, StreamStatePriority, StreamOfflineTimeouts, StreamAlertState2State } = DtConstants;

/** This method assumes the value is the last read value from the entire history, not in a time range */
function evaluateState(thresholds, value, ts, offlineTimeout) {
  if (thresholds == null) return null;
  if (value == null) return StreamStates.NoData;
  if (ts && offlineTimeout !== StreamOfflineTimeouts.Never && ts < nowSec() - offlineTimeout) return StreamStates.Offline;

  return evaluateSimpleState(thresholds, value, value);
}

export function evaluateSimpleState({ lower, upper }, min, max) {
  max ??= min;
  return doEvalSimpleState(lower?.alert, upper?.alert, lower?.warn, upper?.warn, min, max);
}

export function doEvalSimpleState(la, ua, lw, uw, min, max) {
  if (min <= la || max >= ua) return StreamStates.Alert;
  if (min <= lw || max >= uw) return StreamStates.Warning;
  return StreamStates.Normal;
}

/**
 * 
 * @param {Object} stream
 * @param {Map<number, {ts: number, val: number, state: string | null}} readings - A map where the key is dbId and the value connection readings per attribute
 * @param {number} now optional override on what is now expressed in seconds
 * @returns {boolean}
 */
function isOnline(stream, readings, now = nowSec()) {
  const lastUpdate = getLastUpdate(stream, readings);

  if (lastUpdate === null) return false;
  if (stream.settings.offlineTimeout === StreamOfflineTimeouts.Never) return true;

  return now - lastUpdate < stream.settings.offlineTimeout;
}

/**
 * 
 * @param {Object} stream
 * @param {Map<number, {ts: number, val: number, state: string | null}} readings - A map where the key is dbId and the value connection readings per attribute
 * @returns { string } One of StreamStates excl. Offline
 */
function getStatus(stream, readings) {
  const streamReadings = getStreamReadings(stream, readings);

  if (!streamReadings) return StreamStates.NoData;

  return stream.streamAttrs.reduce((currentStatus, attr) => {
    const lastReading = streamReadings[attr.id]?.val;
    const status = evaluateState(attr.allowedValues?.thresholds, lastReading);

    if (currentStatus == null) return status;
    if (StreamStatePriority[status] > StreamStatePriority[currentStatus]) return status;

    return currentStatus;
  }, null);
}

/**
 * 
 * @param {Object} stream
 * @param {Number} value - Reading value
 * @param {number} ts - ts Unix epoch used to decide if allowed to show an offline status (i.e. timeline time).
 * @param {Object} thresholds Object containing `la`, `lw`, `ua`, `uw` stream threshold values
 * @param {object} alert - stream alert data, as given by `getStreamAlertsFromWorker`then filtered by element and attribute
 * @returns { string[] } all applicable stream StreamStates ordered by priority
 */

function getReadingStates(stream, value, ts, thresholds, alert) {
  const states = [];

  if (value == null) return [StreamStates.NoData];

  const offlineTimeout = stream.settings.offlineTimeout;

  if (offlineTimeout !== StreamOfflineTimeouts.Never && ts < nowSec() - offlineTimeout) states.push(StreamStates.Offline);

  if (thresholds) {
    states.push(evaluateSimpleState(thresholds, value));
  } else if (alert) {
    states.push(StreamAlertState2State[alert.currentState] || StreamStates.Normal);
  } else {
    states.push(StreamStates.Normal);
  }

  return states.sort((a, b) => StreamStatePriority[b] - StreamStatePriority[a]);
}

/**
 * Connection-level, offline-aware status, returns the highest-priority state of all streams.
 *
 * Note: It will default to status normal, even if none of the readings have thresholds.
 * @param stream Connection object.
 * @param {Map} data Instant data, as given by `getInstantReadings`
 * @param {number} time Unix epoch used to decide if allowed to show an offline status (i.e. timeline time).
 * @param {number} [now] Optional Date now in seconds.
 * @return {*|string|string}
 */
function getConnectionStateFromInstantData(stream, data, time, now = nowSec()) {
  if (!data) return StreamStates.NoData;

  const states = [];
  let allMissing = true;
  const readingByAid = data.get(stream.dbId);
  for (const attr of stream.streamAttrs) {
    const reading = readingByAid?.[attr.id];
    if (reading == null) {
      states.push(StreamStates.NoData);
    } else {
      allMissing = false;
      const [,, state] = reading;
      states.push(state ?? StreamStates.Normal);
    }
  }

  // If no readings had an active state.
  if (allMissing) {
    const isHistoricalOrFuture = now - time > stream.settings.offlineTimeout || time > now;
    return !states.length || isHistoricalOrFuture ? StreamStates.NoData : StreamStates.Offline;
  }

  // By Priority Desc
  states.sort((a, b) => StreamStatePriority[b] - StreamStatePriority[a]);

  // Return the worst state
  return states[0];
}

/**
 * 
 * @param {Object} stream
 * @param {Map<number, {ts: number, val: number, state: string | null}} readings - A map where the key is dbId and the value connection readings per attribute
 * @returns { number | null }
 */
function getLastUpdate(stream, readings) {
  const streamReadings = getStreamReadings(stream, readings);

  if (!streamReadings) return null;

  return Object.keys(streamReadings).reduce((lts, attrId) => {
    const ts = streamReadings[attrId]?.ts || 0;
    return ts > lts ? ts : lts;
  }, 0);
}

/**
 * 
 * @param {Object} stream
 * @param {Map<number, {ts: number, val: number, state: string | null}} readings - A map where the key is dbId and the value connection readings per attribute
 * @returns { Object | null } stream readings by attribute
 */
function getStreamReadings(stream, readings) {
  if (!readings || !stream) return null;

  const streamReadings = readings.get(stream.dbId);
  return streamReadings && Object.keys(streamReadings).length > 0 ? streamReadings : null;
}

/**
 * Generates a unique, dom-compatible key for a stream based on fullId and an attrId.
 * Useful for having consistent ids when dealing with multi-value streams and their charts.
 * @param {string} fullId The stream fullId
 * @param {string} attrId The attribute ID
 * @returns {string}
 */
export const intoStreamKey = (fullId, attrId) => `c-${fullId}_${attrId.replace(':', '_')}`;

/**
 * Inverse of generateStreamKey
 * @param {string} key Stream key from generateStreamKey
 * @return {{attrId: string, fullId: string}}
 */
export const fromStreamKey = (key) => {
  // 2 for "c-" and 3 for "_z:"
  const attrIdOffset = 2 + ElementIdWithFlagsEncodedLength + 3;
  return {
    fullId: key.substring(2, ElementIdWithFlagsEncodedLength + 2),
    attrId: ColumnFamilies.DtProperties + ':' + key.substring(attrIdOffset)
  };
};

export const StreamUtils = {
  evaluateState,
  getReadingStates,
  getConnectionStateFromInstantData,
  isOnline,
  getStatus,
  getLastUpdate,
  fromStreamKey,
  intoStreamKey,
  // Telemetry specifics
  FocusedTime,
  TimeInterval
};